import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
// Home is the landing route — keep it eager so the first paint is instant.
import { Home } from './pages/Home';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { I18nProvider } from './utils/i18n';
// Every other route is code-split and fetched on demand, so they no longer
// weigh down the initial bundle. Named exports are mapped to a default for lazy.
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Product = lazy(() => import('./pages/Product').then(m => ({
  default: m.Product
})));
const Project = lazy(() => import('./pages/Project').then(m => ({
  default: m.Project
})));
const HelpMeChoose = lazy(() => import('./pages/HelpMeChoose').then(m => ({
  default: m.HelpMeChoose
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({
  default: m.DeliveryZones
})));
const Subcontractors = lazy(() => import('./pages/Subcontractors').then(m => ({
  default: m.Subcontractors
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const DeliveryPrep = lazy(() => import('./pages/DeliveryPrep').then(m => ({
  default: m.DeliveryPrep
})));
const CompareMaterials = lazy(() => import('./pages/CompareMaterials').then(m => ({
  default: m.CompareMaterials
})));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({
  default: m.NotFound
})));
const TrackOrder = lazy(() => import('./pages/TrackOrder').then(m => ({
  default: m.TrackOrder
})));
const TrackOrderLookup = lazy(() => import('./pages/TrackOrderLookup').then(m => ({
  default: m.TrackOrderLookup
})));
const Calculator = lazy(() => import('./pages/Calculator').then(m => ({
  default: m.Calculator
})));
const LocalVsNational = lazy(() => import('./pages/LocalVsNational').then(m => ({
  default: m.LocalVsNational
})));
const CityMaterial = lazy(() => import('./pages/CityMaterial').then(m => ({
  default: m.CityMaterial
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(m => ({
  default: m.ServiceAreas
})));
const LogoConcepts = lazy(() => import('./components/LogoConcepts').then(m => ({
  default: m.LogoConcepts
})));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({
  default: m.RefundPolicy
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const DailyTicketForm = lazy(() => import('./pages/DailyTicketForm').then(m => ({
  default: m.DailyTicketForm
})));
function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf7] dark:bg-[#222222]" role="status" aria-label="Loading" data-id="element-0">
      <span className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" data-id="element-1" />
    </div>;
}
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2">
      <Suspense fallback={<RouteFallback data-id="element-4" />} data-id="element-3">
        <Routes location={location} key={location.pathname} data-id="element-5">
          <Route path="/" element={<PageTransition data-id="element-7">
                <Home data-id="element-8" />
              </PageTransition>} data-id="element-6" />
          <Route path="/contact" element={<PageTransition data-id="element-10">
                <Contact data-id="element-11" />
              </PageTransition>} data-id="element-9" />
          <Route path="/shop/:slug" element={<PageTransition data-id="element-13">
                <Product data-id="element-14" />
              </PageTransition>} data-id="element-12" />
          <Route path="/projects/:slug" element={<PageTransition data-id="element-16">
                <Project data-id="element-17" />
              </PageTransition>} data-id="element-15" />
          <Route path="/help" element={<PageTransition data-id="element-19">
                <HelpMeChoose data-id="element-20" />
              </PageTransition>} data-id="element-18" />
          <Route path="/about" element={<PageTransition data-id="element-22">
                <AboutPage data-id="element-23" />
              </PageTransition>} data-id="element-21" />
          <Route path="/delivery-zones" element={<PageTransition data-id="element-25">
                <DeliveryZones data-id="element-26" />
              </PageTransition>} data-id="element-24" />
          <Route path="/subcontractors" element={<PageTransition data-id="element-28">
                <Subcontractors data-id="element-29" />
              </PageTransition>} data-id="element-27" />
          <Route path="/contractor-ordering" element={<PageTransition data-id="element-31">
                <Subcontractors data-id="element-32" />
              </PageTransition>} data-id="element-30" />
          <Route path="/diy-help" element={<Navigate to="/blog/perfect-gravel-driveway" replace data-id="element-34" />} data-id="element-33" />
          <Route path="/blog" element={<PageTransition data-id="element-36">
                <Blog data-id="element-37" />
              </PageTransition>} data-id="element-35" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-39">
                <BlogPost data-id="element-40" />
              </PageTransition>} data-id="element-38" />
          <Route path="/delivery-prep" element={<PageTransition data-id="element-42">
                <DeliveryPrep data-id="element-43" />
              </PageTransition>} data-id="element-41" />
          <Route path="/compare" element={<PageTransition data-id="element-45">
                <CompareMaterials data-id="element-46" />
              </PageTransition>} data-id="element-44" />
          <Route path="/calculator" element={<PageTransition data-id="element-48">
                <Calculator data-id="element-49" />
              </PageTransition>} data-id="element-47" />
          <Route path="/local-vs-national" element={<PageTransition data-id="element-51">
                <LocalVsNational data-id="element-52" />
              </PageTransition>} data-id="element-50" />
          <Route path="/track" element={<PageTransition data-id="element-54">
                <TrackOrderLookup data-id="element-55" />
              </PageTransition>} data-id="element-53" />
          <Route path="/track/:orderId" element={<PageTransition data-id="element-57">
                <TrackOrder data-id="element-58" />
              </PageTransition>} data-id="element-56" />
          <Route path="/service-areas" element={<PageTransition data-id="element-60">
                <ServiceAreas data-id="element-61" />
              </PageTransition>} data-id="element-59" />
          <Route path="/logo-concepts" element={<PageTransition data-id="element-63">
                <LogoConcepts data-id="element-64" />
              </PageTransition>} data-id="element-62" />
          <Route path="/refund-policy" element={<PageTransition data-id="element-66">
                <RefundPolicy data-id="element-67" />
              </PageTransition>} data-id="element-65" />
          <Route path="/privacy-policy" element={<PageTransition data-id="element-69">
                <PrivacyPolicy data-id="element-70" />
              </PageTransition>} data-id="element-68" />
          <Route path="/terms-of-service" element={<PageTransition data-id="element-72">
                <TermsOfService data-id="element-73" />
              </PageTransition>} data-id="element-71" />
          <Route path="/form" element={<PageTransition data-id="element-75">
                <DailyTicketForm data-id="element-76" />
              </PageTransition>} data-id="element-74" />

          <Route path="/gravel-delivery/:citySlug/:materialSlug" element={<PageTransition data-id="element-78">
                <CityMaterial data-id="element-79" />
              </PageTransition>} data-id="element-77" />
          <Route path="*" element={<PageTransition data-id="element-81">
                <NotFound data-id="element-82" />
              </PageTransition>} data-id="element-80" />
        </Routes>
      </Suspense>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The daily ticket form (/form) is a standalone operational document. It
  // renders bare — no site chrome (navbar, footer, announcement bar, live
  // chat, sticky CTA, cart) — so it prints clean and stays out of the way.
  const isStandalonePage = location.pathname === '/form';
  if (isStandalonePage) {
    // The daily ticket is a printed white document — never apply the `dark`
    // class here so it always renders on a clean white surface regardless of
    // the visitor's theme.
    return <div className="min-h-screen font-sans" data-id="element-83">
        <ScrollToHash data-id="element-84" />
        <AnimatedRoutes data-id="element-85" />
      </div>;
  }
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-86">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-87">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-88">
        <ScrollToHash data-id="element-89" />
        <AnnouncementBar data-id="element-90" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-91" />
        <AnimatedRoutes data-id="element-92" />
        <Footer data-id="element-93" />
        <LiveChat data-id="element-94" />
        <StickyMobileCTA data-id="element-95" />
        <ScrollToTopButton data-id="element-96" />
        <CartDrawer data-id="element-97" />
      </div>
    </div>;
}
// Decide the initial theme for a first-time visitor (no saved preference yet):
//   1. An explicit saved choice always wins.
//   2. Otherwise honor the device's OS setting (prefers-color-scheme).
//   3. If the device expresses no preference, fall back to the local clock:
//      light during daylight hours (roughly 6am–7pm), dark after sunset.
function getInitialTheme(): boolean {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
  } catch {
    // localStorage can throw in private mode / embedded webviews / when
    // storage is blocked. Fall through to the device/time defaults.
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return true;
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return false;
  }
  // Time-of-day fallback: dark after sunset / before sunrise.
  const hour = new Date().getHours();
  return hour < 6 || hour >= 19;
}
export function App() {
  const [isDark, setIsDark] = useState(getInitialTheme);
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {}
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-98">
      <Router data-id="element-99">
        <CartProvider data-id="element-100">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-101" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}